import { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useProperty } from '../contexts/PropertyContext';
import { ShieldOff } from 'lucide-react';

/**
 * ProtectedRoute — blocks rendering until session is validated against the server.
 *
 * Flow:
 *  1. If no tokens in store → redirect to login immediately (no flash).
 *  2. If tokens exist → show a minimal spinner while calling GET /auth/me.
 *  3. If /me succeeds → render children.
 *  4. If /me fails (401 / network) → clearAuth & redirect to login.
 *
 * This prevents stale localStorage from granting access to protected pages.
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const fetchUser = useAuthStore((s) => s.fetchUser);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const location = useLocation();

  // "verified" means we've confirmed the token is valid with the server
  const [sessionVerified, setSessionVerified] = useState(false);
  const [sessionFailed, setSessionFailed] = useState(false);
  const verifyRan = useRef(false);

  useEffect(() => {
    if (verifyRan.current) return;
    verifyRan.current = true;

    if (!isAuthenticated || !accessToken) {
      setSessionFailed(true);
      return;
    }

    // Validate token with backend
    fetchUser()
      .then(() => setSessionVerified(true))
      .catch(() => {
        clearAuth();
        setSessionFailed(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // No tokens at all → redirect immediately
  if (sessionFailed || (!isAuthenticated && !accessToken)) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Waiting for server validation
  if (!sessionVerified) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * PublicRoute — only accessible when NOT authenticated.
 *
 * Validates tokens before auto-redirecting to prevent stale localStorage
 * from automatically logging users in.
 */
export function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, accessToken, fetchUser, clearAuth } = useAuthStore();
  const [isValidating, setIsValidating] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const validatedRef = useRef(false);

  useEffect(() => {
    if (validatedRef.current) return;
    validatedRef.current = true;

    // If no tokens, show login page immediately
    if (!isAuthenticated || !accessToken) {
      setIsValidating(false);
      return;
    }

    // Validate token with backend before auto-redirecting
    fetchUser()
      .then(() => {
        // Token is valid, redirect to dashboard
        setShouldRedirect(true);
        setIsValidating(false);
      })
      .catch(() => {
        // Token is invalid/expired, clear auth and show login
        clearAuth();
        setIsValidating(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Show loading while validating existing tokens
  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
      </div>
    );
  }

  // Redirect to dashboard if validated
  if (shouldRedirect) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show login/register page
  return <>{children}</>;
}

/**
 * AccessGuard — wraps a page that requires a specific service type grant.
 * Owners always pass through. Family members must have the serviceType in
 * their grantedServiceTypes list. Shows a friendly "no access" screen if not.
 *
 * Usage: <Route path={ROUTES.MAINTENANCE} element={<AccessGuard serviceType="maintenance"><Maintenance /></AccessGuard>} />
 */
export function AccessGuard({
  children,
  serviceType,
}: {
  children: React.ReactNode;
  serviceType: string;
}) {
  const { t } = useTranslation('common');
  const { hasServiceAccess, isLoading } = useProperty();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
      </div>
    );
  }

  if (!hasServiceAccess(serviceType)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        <div className="text-center max-w-sm space-y-4">
          <div className="flex justify-center">
            <div className="h-16 w-16 rounded-2xl bg-secondary flex items-center justify-center">
              <ShieldOff className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
          <div className="space-y-1">
            <h2 className="text-xl font-medium">{t('guard.accessRestrictedTitle')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('guard.accessRestrictedBody')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
